// src/contexts/TombolaContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { ecouterTombolas, ecouterMembresTombola } from "../services/db";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "./AuthContext";

const TombolaContext = createContext();
export const useTombola = () => useContext(TombolaContext);

export const STARTER_LIMIT = 100;
export const STARTER_WARNING = 80;

export function TombolaProvider({ children }) {
  const { orgId, currentUser, isAdmin } = useAuth();
  const [tombolas, setTombolas] = useState([]);
  const [activeTombolaId, setActiveTombolaId] = useState(null);
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);

  // Association pour laquelle la liste actuelle a été chargée.
  // Tant qu'elle ne correspond pas à l'association active, on considère que ça charge :
  // évite d'afficher un instant les tombolas de l'association précédente.
  const [chargePour, setChargePour] = useState(null);

  // Écouter les tombolas de l'org
  useEffect(() => {
    if (!orgId || !currentUser) { setLoading(false); return; }
    setLoading(true);

    // Tombola à présélectionner (ex. bénévole qui vient d'accepter une invitation)
    const cleSaved = `tl_activeTombola_${currentUser.uid}`;
    const savedId = localStorage.getItem(cleSaved);

    // L'admin de l'association voit toutes les tombolas.
    // Un bénévole ne voit QUE les tombolas dont il est membre (accès par tombola).
    const acces = {}; // tombolaId → membre ou non (évite de relire à chaque mise à jour)
    let annule = false;

    async function estMembre(tombolaId) {
      if (tombolaId in acces) return acces[tombolaId];
      try {
        const snap = await getDoc(doc(db, "organisations", orgId, "tombolas", tombolaId, "membres", currentUser.uid));
        acces[tombolaId] = snap.exists();
      } catch {
        acces[tombolaId] = false; // pas membre → lecture refusée par les règles
      }
      return acces[tombolaId];
    }

    const unsub = ecouterTombolas(orgId, async (list) => {
      let visibles = list;
      if (!isAdmin) {
        const membre = await Promise.all(list.map((t) => estMembre(t.id)));
        visibles = list.filter((_, i) => membre[i]);
      }
      if (annule) return;
      setTombolas(visibles);
      setActiveTombolaId((prev) => {
        if (savedId && visibles.find((t) => t.id === savedId)) {
          localStorage.removeItem(cleSaved);
          return savedId;
        }
        if (prev && visibles.find((t) => t.id === prev)) return prev;
        const active = visibles.find((t) => !t.archived);
        return active ? active.id : (visibles[0]?.id || null);
      });
      setChargePour(orgId);
      setLoading(false);
    }, () => {
      // Lecture refusée ou erreur réseau : on n'affiche rien plutôt que de bloquer
      if (annule) return;
      setTombolas([]);
      setActiveTombolaId(null);
      setChargePour(orgId);
      setLoading(false);
    });

    return () => { annule = true; unsub(); };
  }, [orgId, currentUser, isAdmin]);

  // Écouter les membres de la tombola active
  useEffect(() => {
    if (!orgId || !activeTombolaId || chargePour !== orgId) { setMembres([]); return; }
    const unsub = ecouterMembresTombola(orgId, activeTombolaId, (list) => {
      setMembres(list);
    });
    return unsub;
  }, [orgId, activeTombolaId, chargePour]);

  const aJour = chargePour === orgId;
  const tombolasVisibles = aJour ? tombolas : [];
  const activeTombolaIdVisible = aJour ? activeTombolaId : null;
  const activeTombola = tombolasVisibles.find((t) => t.id === activeTombolaIdVisible) || null;

  // Plan de la tombola active
  const isPro = activeTombola?.plan === "pro";
  const isStarter = !isPro;

  // Rôle de l'utilisateur dans la tombola active
  const monMembre = membres.find((m) => m.userId === currentUser?.uid || m.id === currentUser?.uid);
  const isAdminTombola = monMembre?.role === "admin";
  const isBenevole = monMembre?.role === "benevole";

  // Vérification du quota Starter
  function checkQuota(totalBillets) {
    if (isPro) return { ok: true };
    if (totalBillets >= STARTER_LIMIT) {
      return {
        ok: false,
        message: `Limite Starter atteinte (${STARTER_LIMIT} billets). Passez en Pro pour continuer à vendre.`,
      };
    }
    if (totalBillets >= STARTER_WARNING) {
      return {
        ok: true,
        warning: `Attention : ${STARTER_LIMIT - totalBillets} billets restants sur votre quota Starter.`,
      };
    }
    return { ok: true };
  }

  return (
    <TombolaContext.Provider value={{
      tombolas: tombolasVisibles,
      activeTombola,
      activeTombolaId: activeTombolaIdVisible,
      setActiveTombolaId,
      membres,
      monMembre,
      isAdminTombola,
      isBenevole,
      loading: loading || (!!orgId && !!currentUser && !aJour),
      isPro,
      isStarter,
      checkQuota,
      STARTER_LIMIT,
    }}>
      {children}
    </TombolaContext.Provider>
  );
}
